import { useEffect } from 'react'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'

interface UsePredictionShortcutsParams {
  isActive: boolean
  isResizing: () => boolean
  allBoxes: AnnotationDataBoundingBox[]
  getCurrentSelectedBox: () => AnnotationDataBoundingBox | undefined
  acceptBox: (box: AnnotationDataBoundingBox) => void
  rejectBox: (box: AnnotationDataBoundingBox) => void
  onNextImage: () => void
  onPrevImage: () => void
  onSubmitIfDone: () => void
}

export function usePredictionShortcuts({
  isActive,
  isResizing,
  allBoxes,
  getCurrentSelectedBox,
  acceptBox,
  rejectBox,
  onNextImage,
  onPrevImage,
  onSubmitIfDone,
}: UsePredictionShortcutsParams): void {
  useEffect(() => {
    if (!isActive) return
    const handler = (e: KeyboardEvent) => {
      if (isResizing()) return
      // Ignore when typing in editable fields
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        const isEditableEl =
          target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
        if (isEditableEl) return
      }

      const key = e.key

      // Submit current review if applicable
      if (key === 'Enter') {
        e.preventDefault()
        onSubmitIfDone()
        return
      }

      // Navigation: support Ctrl/Cmd+N (next), Ctrl/Cmd+P (prev)
      if (e.shiftKey && (key === 'N' || key === 'n')) {
        e.preventDefault()
        onNextImage()
        return
      }
      if (e.shiftKey && (key === 'P' || key === 'p')) {
        e.preventDefault()
        onPrevImage()
        return
      }

      // Accept/Reject current selection
      if (key === 'a' || key === 'A') {
        e.preventDefault()
        const current = getCurrentSelectedBox()
        if (current) acceptBox(current)
        return
      }
      if (key === 'r' || key === 'R') {
        e.preventDefault()
        const current = getCurrentSelectedBox()
        if (current) rejectBox(current)
        return
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    isActive,
    isResizing,
    allBoxes,
    getCurrentSelectedBox,
    acceptBox,
    rejectBox,
    onNextImage,
    onPrevImage,
    onSubmitIfDone,
  ])
}
